<template>
  <!-- Legacy app rail (ui_layout === 'V2'). Restyled to match the prototype /
       rm-portal-beta AppRail: a fixed 64px dark rail with icon-over-label items
       and an active glow. Top-level only — sub-items live in the page ToC.
       Fed by the shared v3 nav model (useV3Nav). Container groups (Admin /
       Reporting) navigate to their default child. -->
  <nav class="side-rail" aria-label="Primary">
    <!-- Contextual ToC drawer collapse/expand toggle (mirrors the prototype;
         drives the shared chrome state the drawer consumes in Phase 2). -->
    <button
      class="side-rail__toggle"
      type="button"
      :title="sidebarCollapsed ? 'Show panel' : 'Hide panel'"
      :aria-label="sidebarCollapsed ? 'Show panel' : 'Hide panel'"
      :aria-pressed="!sidebarCollapsed"
      @click="toggleSidebar"
    >
      <span class="side-rail__ms">{{ sidebarCollapsed ? 'left_panel_open' : 'left_panel_close' }}</span>
    </button>

    <div class="side-rail__items">
      <button
        v-for="item in railItems"
        :key="item.key"
        type="button"
        class="side-rail__item"
        :class="{ active: activeArea === item.key }"
        :title="item.text"
        :aria-current="activeArea === item.key ? 'page' : undefined"
        @click="go(item)"
      >
        <span class="side-rail__icwrap"><span class="side-rail__ms">{{ item.icon }}</span></span>
        <span class="side-rail__lbl">{{ item.text }}</span>
      </button>
    </div>
    <span class="side-rail__spacer" />
  </nav>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { useV3Nav } from '@/composables/useV3Nav';
import { useV3Chrome } from '@/composables/useV3Chrome';

export default {
  name: 'SideMenu',

  setup() {
    const instance = getCurrentInstance().proxy;

    const { railItems, activeArea } = useV3Nav({
      router: instance.$router,
      loginState: instance.$loginState,
      getCurrentRouteName: () => instance.$route && instance.$route.name,
    });
    const { sidebarCollapsed, toggleSidebar } = useV3Chrome();

    function go(item) {
      if (!item || !item.routeName) return;
      if (item.routeName === instance.$route.name) return;
      // Preserve the :user route param (all v3 pages live under /u/:user/...).
      instance.$router
        .push({ name: item.routeName, params: { ...instance.$route.params } })
        .catch(() => {});
    }

    return { railItems, activeArea, go, sidebarCollapsed, toggleSidebar };
  },
};
</script>

<style lang="scss" scoped>
// Prototype rail palette (rm-portal-beta v3-tokens: --v3-rail / -fg / -fg-dim).
$rail-bg: #16181d;
$rail-fg: #b7bdc9;
$rail-fg-dim: #777e8b;

.side-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  min-height: 0;
  width: 64px;
  background: $rail-bg;
  // Start the rail's content BELOW the header (dense app-bar = 48px) so the nav
  // sits under the top bar like the beta prototype. The rail's dark top strip
  // merges with the (also-dark) header into one full-width top bar.
  padding: 56px 0 8px;
  overflow: hidden;

  // Material Symbols Rounded (loaded in index.html) — the glyph font the rail
  // icons render with (Vuetify's default mdi font can't render these ligatures).
  &__ms {
    font-family: 'Material Symbols Rounded';
    font-weight: normal;
    font-style: normal;
    font-size: 21px;
    line-height: 1;
    display: inline-flex;
    white-space: nowrap;
    letter-spacing: normal;
    text-transform: none;
    -webkit-font-smoothing: antialiased;
    font-feature-settings: 'liga';
    font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
    user-select: none;
  }

  &__toggle {
    width: 40px;
    height: 30px;
    flex: 0 0 auto;
    margin-bottom: 6px;
    border: 0;
    background: transparent;
    color: $rail-fg-dim;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: color 120ms, background 120ms;

    &:hover { color: #fff; background: rgb(255 255 255 / 0.08); }
    &:focus-visible { outline: none; box-shadow: 0 0 0 2px #a78bfa; }
    .side-rail__ms { font-size: 20px; }
  }

  &__items {
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    overflow-y: auto;
    overflow-x: hidden;

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb { background: rgb(255 255 255 / 0.18); border-radius: 2px; }
    &::-webkit-scrollbar-track { background: transparent; }
  }

  &__item {
    width: 56px;
    flex: 0 0 auto;
    border: 0;
    background: transparent;
    color: $rail-fg-dim;
    padding: 6px 0 5px;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    cursor: pointer;
    font-family: inherit;
    transition: color 120ms;

    &:hover {
      color: $rail-fg;
      .side-rail__icwrap { background: rgb(255 255 255 / 0.08); }
    }

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px #a78bfa;
    }

    &.active {
      color: #fff;
      .side-rail__icwrap { background: rgb(255 255 255 / 0.12); }
    }
  }

  &__icwrap {
    width: 32px;
    height: 32px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    transition: background 120ms;
  }

  &__lbl {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.01em;
    line-height: 1.1;
    text-align: center;
    max-width: 60px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__spacer { flex: 1 1 auto; }

  // Active glow behind the icon — pulses bright then settles (prototype parity).
  &__item.active .side-rail__icwrap::before {
    content: '';
    position: absolute;
    inset: -5px;
    border-radius: inherit;
    background: conic-gradient(from 210deg, #4f46e5, #4aa8ff, #7c5cff, #a78bfa, #4f46e5);
    filter: blur(12px);
    opacity: 0.55;
    z-index: 0;
    animation: side-rail-glow 3.2s ease 0.3s forwards;
  }
  &__item.active .side-rail__ms { position: relative; z-index: 1; }
}

@keyframes side-rail-glow {
  from { opacity: 0.55; }
  to { opacity: 0.14; }
}
@media (prefers-reduced-motion: reduce) {
  .side-rail__item.active .side-rail__icwrap::before { animation: none; opacity: 0.14; }
}
</style>

<!-- Glyph font declared globally (unscoped) so it applies to the rail's Material
     Symbols spans regardless of scoped-attribute matching — same font the v3
     landing widgets use. -->
<style lang="scss">
.side-rail__ms {
  font-family: 'Material Symbols Rounded' !important;
  font-weight: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  -webkit-font-smoothing: antialiased;
  font-feature-settings: 'liga';
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  user-select: none;
}
</style>
